import type { FunctionComponent } from 'react';

import Button from '../../../generic/Button/Button';
import Flex from '../../../generic/Flex';
import FormGroup from '../../../generic/Forms/FormGroup';
import FormLabel from '../../../generic/Forms/FormLabel';
import MultiTextInput from '../../../generic/Forms/MultiTextInput';
import TextInput from '../../../generic/Forms/TextInput';
import Icon, { icons, IconText } from '../../../generic/Icon';
import Text from '../../../generic/Text';
import Toggle from '../../../generic/Toggle';
import Tooltip from '../../../generic/Tooltip';
import type { JiraResponse } from '../../../../../shared/jira';

import JiraSettingsSection from './JiraSettingsSection';

export type JiraSettingsStatusProps = Pick<
  JiraResponse, 'webhookURL' | 'resolvedStatusesEnabled' | 'resolved_statuses'
> & {
  onStatusesChange: (statuses: string[]) => void;
  onStatusesEnabledChange: (enabled: boolean) => void;
  onRegenerateWebhook: () => void;
};

/**
 * Component to update Jira specific settings, like the webhook url and resolved statuses
 */
const JiraSettingsStatus: FunctionComponent<JiraSettingsStatusProps> = ({
  resolved_statuses: statuses,
  onStatusesChange,
  onStatusesEnabledChange,
  resolvedStatusesEnabled: isStatusesEnabled,
  webhookURL: webhook,
  onRegenerateWebhook,
}) => (
  <JiraSettingsSection heading="Status">
    <FormGroup>
      <div>
        <Flex justify="flex-start" align="flex-start">
          <div className="margin-right-sm margin-bottom-sm">Resolved workflow statuses</div>
          <Toggle
            onChange={onStatusesEnabledChange}
            checked={isStatusesEnabled}
          />
          <Tooltip
            id="resolved-status-enabled-toggle"
            tooltip={
              'If enabled, FOSSA will use the below statuses to determine if a FOSSA issue should be ignored (configured below) or made active (issue status is NOT configured below). ' +
              'If disabled, FOSSA will mark an issue as ignored based on the \'Done\' status(es) (configured in Jira)'
            }
            placement="right"
          >
            <span className="inline-block margin-left-xs">
              <Icon icon={icons.infoCircle} />
            </span>
          </Tooltip>
        </Flex>
        <MultiTextInput
          id="statuses"
          isDisabled={!isStatusesEnabled}
          label="Resolved workflow statuses"
          placeholder="Add statuses..."
          onChange={onStatusesChange}
          values={statuses}
        />
      </div>
      <div>
        <FormLabel variant="legacy" label="Webhook URL">
          <div className="jira-settings__status__webhook">
            <TextInput
              label="Webhook URL"
              placeholder="This will be generated automatically once this page is saved."
              value={webhook || ''}
              isDisabled
            />
            <span className="margin-left-sm">
              <Button
                type="secondary"
                size="large"
                isDisabled={!webhook}
                onClick={onRegenerateWebhook}
              >
                <IconText
                  icon={icons.refresh}
                  text="Regenerate"
                />
              </Button>
            </span>
          </div>
        </FormLabel>
        <Text.Micro>
          Locator used to automatically sync states between Jira and FOSSA (related to resolved workflow statuses).
        </Text.Micro>
      </div>
    </FormGroup>
  </JiraSettingsSection>
);

export default JiraSettingsStatus;
